import { Alert, Descriptions, Form, Input, Modal, Tooltip, Typography } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  approveStockApproval,
  cancelStockApproval,
  isCountedQuantity,
  rejectStockApproval,
  type ApprovalAction,
  type StockApproval,
  type StockOperation,
} from './stockApi'

/**
 * Qaror natijasi — IKKI XIL SHAKL, chunki server ham ikki xil qaytaradi.
 *
 * ⚠️ Tasdiqlash `StockOperation` beradi (harakat SHU PAYTDA yozildi va
 * `lowStock` ogohlantirishi ham shu javobda chiqishi mumkin), rad etish
 * va bekor qilish esa faqat yangilangan so'rovni. Ularni bitta shaklga
 * "tekislash" tasdiqlash javobidagi jurnal qatorlarini va past qoldiq
 * belgisini yo'qotardi.
 */
export type ApprovalDecisionResult =
  | { action: 'APPROVE'; operation: StockOperation; approval: StockApproval }
  | { action: 'REJECT' | 'CANCEL'; approval: StockApproval }

interface Props {
  action: ApprovalAction
  approval: StockApproval
  /** Ekranga chiqadigan nomlar — sahifada bir marta yig'iladi */
  itemName: string
  itemUnit: string
  warehouseName: string
  onClose: () => void
  onDone: (result: ApprovalDecisionResult) => void
}

interface FormValues {
  note?: string
}

/**
 * Ikkinchi imzo qarori — tasdiqlash, rad etish, bekor qilish.
 *
 * <h2>⚠️ UCHTA AMAL, BITTA OYNA — LEKIN QOIDALARI BOSHQA</h2>
 * <ul>
 *   <li><b>Tasdiqlash</b> — so'ragan odamdan BOSHQA kishi
 *       (`StockException.SelfApproval` → 403). Sahifa bu holatda
 *       tugmani ochmaydi va SABABINI yozadi.</li>
 *   <li><b>Rad etish</b> — o'z so'roviga ham ochiq: backendda
 *       `rejectApproval` da o'zini o'zi tekshirish YO'Q va bu mantiqan
 *       to'g'ri (o'z so'rovini rad etish — uni bekor qilish bilan bir
 *       xil natija).</li>
 *   <li><b>Bekor qilish</b> — FAQAT so'rovni yozgan odam
 *       (`StockException.NotRequester` → 403). Bu yo'l HAR DOIM ochiq
 *       va aynan u "tasdiqlovchi yo'q bo'lsa ombor qotib qoladi" degan
 *       xavfni yopadi.</li>
 * </ul>
 *
 * <h2>⚠️ TERIB TASDIQLASH YO'Q — va bu ONGLI farq</h2>
 * `StockMovementModal` da ombor kodini terish TALAB QILINADI, chunki u
 * yerda operator o'z amalini o'zi yozadi va uni hech kim ko'rmaydi. Bu
 * yerda esa amal ALLAQACHON ikkinchi odam tomonidan yozilgan, oyna
 * uning to'liq mazmunini ko'rsatib turibdi va bosayotgan kishi aynan
 * TEKSHIRUVCHI. `VoucherVoidModal` shu farqni ta'riflagan: terish
 * qorovul bo'lishi uchun u yangi ma'lumot qo'shishi kerak — bu yerda
 * qo'shmaydi, faqat marosim bo'lardi.
 *
 * <h2>⚠️ SO'ROVNI TAHRIRLAB BO'LMAYDI</h2>
 * Miqdor, sabab va ombor bu yerda O'ZGARTIRILMAYDI (`ApprovalDecision`
 * da bunday maydonlar umuman yo'q, bazadagi
 * `stock_approval_state_guard` esa buni majburlaydi). Aks holda "men
 * boshqa narsani ko'rgan edim" degan holat paydo bo'lardi.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * `onError` YO'Q: 403 («o'z so'rovingizni tasdiqlay olmaysiz») va 409
 * («qaror allaqachon qabul qilingan») — aynan shu oynada o'qilishi
 * kerak bo'lgan matnlar. Yopishda `decide.reset()`.
 */
export function StockApprovalDecisionModal({
  action,
  approval,
  itemName,
  itemUnit,
  warehouseName,
  onClose,
  onDone,
}: Props) {
  const { t } = useTranslation()
  // ⚠️ B4: ikkinchi imzo ekranida so'rov egasi XOM UUID bo'lib
  //    ko'rinardi — aynan tasdiqlash oldidan, ya'ni «kimning
  //    so'rovini imzolayapman» degan savol javobsiz qolardi.
  const actors = useActorDirectory()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  const decide = useMutation({
    mutationFn: async (values: FormValues): Promise<ApprovalDecisionResult> => {
      const note = values.note?.trim() ? values.note.trim() : null
      if (action === 'APPROVE') {
        const operation = await approveStockApproval(approval.id, note)
        // ⚠️ Javobdagi `approval` USTUN: unda qaror vaqti va kim
        // tasdiqlagani turadi. Kelmasa eski (PENDING) yozuv qoladi va
        // sahifa uni "hali kutmoqda" deb ko'rsatardi.
        return { action, operation, approval: operation.approval ?? approval }
      }
      const updated =
        action === 'REJECT'
          ? await rejectStockApproval(approval.id, note)
          : // ⚠️ Bekor qilish izoh OLMAYDI — backend tanasiz `POST`.
            await cancelStockApproval(approval.id)
      return { action, approval: updated }
    },
    onSuccess: (result) => {
      // ⚠️ `stock.all` PREFIKSI: tasdiqlash HARAKAT yozadi, ya'ni
      // qoldiq, jurnal va zaxira qiymati ham eskiradi — ro'yxatning
      // o'zi bilan birga.
      void queryClient.invalidateQueries({ queryKey: queryKeys.stock.all })
      form.resetFields()
      onDone(result)
      onClose()
    },
  })

  const close = () => {
    decide.reset()
    onClose()
  }

  const counted = isCountedQuantity(approval.kind)

  return (
    <Modal
      open
      title={t(`stock.approval.action.${action}.title`)}
      onCancel={close}
      // ⚠️ Bekor qilishda forma UMUMAN chizilmaydi (izoh yo'q), shuning
      // uchun `validateFields()` ham chaqirilmaydi: bog'lanmagan
      // `useForm` nusxasida u ogohlantirish beradi va amalni
      // to'sib qo'yishi mumkin.
      onOk={() =>
        action === 'CANCEL'
          ? decide.mutate({})
          : submitOnValid(form, (values) => decide.mutate(values))
      }
      confirmLoading={decide.isPending}
      okText={t(`stock.approval.action.${action}.submit`)}
      okButtonProps={{ danger: action !== 'APPROVE' }}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={640}
    >
      <Alert
        type={action === 'APPROVE' ? 'warning' : 'info'}
        showIcon
        style={{ marginBottom: 16 }}
        message={t(`stock.approval.action.${action}.note`)}
      />

      {/* 403 va 409 matnlari SHU YERDA qoladi — sahifa banneriga
          chiqarilsa, ular modal niqobi ortida ko'rinmasdi */}
      {decide.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(decide.error, t)}
        />
      ) : null}

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label={t('stock.field.kind')}>
          {t(`movementKind.${approval.kind}`)}
        </Descriptions.Item>
        <Descriptions.Item label={t('stock.field.item')}>{itemName}</Descriptions.Item>
        <Descriptions.Item label={t('stock.field.warehouse')}>{warehouseName}</Descriptions.Item>
        <Descriptions.Item
          label={
            counted ? t('stock.approval.countedQuantity') : t('stock.approval.outQuantity')
          }
        >
          {`${String(approval.quantity)} ${itemUnit}`}
        </Descriptions.Item>
        <Descriptions.Item label={t('stock.approval.estimatedCost')}>
          {formatMoneyValue(approval.estimatedCost)}
        </Descriptions.Item>
        <Descriptions.Item label={t('stock.approval.thresholdAtRequest')}>
          {formatMoneyValue(approval.threshold)}
        </Descriptions.Item>
        <Descriptions.Item label={t('stock.field.reason')}>
          {/* ⚠️ Sabab MAJBURIY maydon va u uzun bo'lishi mumkin —
              «2 qator + Batafsil». Qisqa matnda tugma umuman
              chizilmaydi (`ExpandableHint` o'zi o'lchaydi). */}
          {approval.reason === null ? (
            t('common.dash')
          ) : (
            <ExpandableHint text={approval.reason} fontSize={14} />
          )}
        </Descriptions.Item>
        {approval.assetTag === null ? null : (
          <Descriptions.Item label={t('stock.field.assetTag')}>{approval.assetTag}</Descriptions.Item>
        )}
        <Descriptions.Item label={t('stock.approval.requestedBy')}>
          {actorLabel(approval.requestedBy, actors, t('common.dash'))}
        </Descriptions.Item>
        <Descriptions.Item label={t('stock.approval.requestedAt')}>
          <Tooltip title={utcTooltip(approval.requestedAt)}>
            <span>{formatDateTime(approval.requestedAt)}</span>
          </Tooltip>
        </Descriptions.Item>
      </Descriptions>

      {action === 'APPROVE' ? (
        <Typography.Paragraph type="secondary">
          {t('stock.approval.recalcNote')}
        </Typography.Paragraph>
      ) : null}

      {/* ⚠️ Bekor qilishda izoh maydoni CHIZILMAYDI: backend uni olmaydi
          (tanasiz `POST`). Maydonni ko'rsatib qiymatni tashlab yuborish
          "yozdim — saqlanmadi" degan yolg'on bo'lardi. */}
      {action === 'CANCEL' ? null : (
        <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={decide.isPending}>
          <Form.Item
            name="note"
            label={t('stock.approval.note')}
            extra={
              action === 'REJECT'
                ? t('stock.approval.noteRejectHint')
                : t('stock.approval.noteApproveHint')
            }
            rules={[{ max: 500, message: t('stock.validation.reasonLength') }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}
